import { useMutation } from '@apollo/client';
import { Box, Button, Stack } from '@mui/material';
import { CuriosityNamespaceType, useCuriosityConfig } from '@procureai/curiosity-ui-mui';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { Operator } from '@procureai/query-builder';
import { FormSchemaProvider, TextAreaFieldWithController } from '@procureai/ui-form-mui';
import {
  CoverageAssistantFilters,
  CoverageAssistantFiltersPayload,
  CoverageAssistantFiltersPayloadErrorCode,
  toCoverageAssistantFiltersPayload,
} from '@procureai/ui-modules-mui';
import { useCustomSnackbar, useUuid } from '@procureai/ui-mui';
import { isNil } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CONTRACT_DEMAND_CREATE_FROM_COVERAGE_WITH_RULE_BUILDER } from '../../CoverageAssistantWidget.mutation.gql';
import { CommodityUndercoverages, commodityUndercoveragesSchema } from '../../CoverageAssistantWidget.schema';
import { Currency, TsrctContractDemandFilterField } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { encodeQueryRules } from '~/hooks';
import { RouteId } from '~/routes';

import { CoverageAssistantPredictionsGrid } from './CoverageAssistantPredictionsGrid';

interface CoverageAssistantPredictionsProps {
  filters: CoverageAssistantFilters | null;
}

interface NormalizedFiltersState {
  payload: CoverageAssistantFiltersPayload;
  errorCode: CoverageAssistantFiltersPayloadErrorCode | null;
  canQuery: boolean;
  canSubmit: boolean;
}

const isFiniteNumber = (value: unknown): value is number => !isNil(value) && Number.isFinite(value);

const getCommodityGroupPath = (
  commodityGroupId: string,
  cache: ReturnType<typeof useCuriosityConfig>['cache'],
): string[] | null => {
  const translation = cache.get(CuriosityNamespaceType.COMMODITY_GROUP, commodityGroupId);
  const path = translation?.entity?.path;
  if (!Array.isArray(path)) return null;
  if (!path.every((item) => typeof item === 'string')) return null;
  return path;
};
const MULTIPLE_TOP_LEVEL_PATH_ERROR_CODE: CoverageAssistantFiltersPayloadErrorCode = 'multipleTopLevelPathError';

interface CoverageAssistantPredictionsFormProps {
  normalizedFilters: NormalizedFiltersState | null;
  createLoading: boolean;
}

const CoverageAssistantPredictionsForm = ({
  normalizedFilters,
  createLoading,
}: CoverageAssistantPredictionsFormProps) => {
  const { hasPermission } = usePermissions();
  const { t } = useTranslation(I18nNamespace.common, { keyPrefix: 'CoverageAssistant' });
  const { t: tWidget } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CoverageAssistantWidget' });
  const { watch, control } = useFormContext<CommodityUndercoverages>();

  // Watch for selected predictions from the FormSchemaProvider context
  const predictions = watch('predictions');
  const selectedCount = predictions.filter((p) => p.selected === true).length;
  return (
    <Stack spacing={4} gap={4}>
      <CoverageAssistantPredictionsGrid normalizedFilters={normalizedFilters} />

      <TextAreaFieldWithController
        name="summary"
        control={control}
        label={tWidget('summaryLabel')}
        placeholder={tWidget('summaryPlaceholder')}
        rows={3}
        required
      />
      <Box display="flex">
        <Button
          variant="contained"
          disabled={
            !hasPermission(Permission.ContractDemandCreate) ||
            createLoading ||
            selectedCount === 0 ||
            !normalizedFilters?.canSubmit
          }
          loading={createLoading}
          type="submit"
          color="primary"
        >
          {t('button.createDemand')}
        </Button>
      </Box>
    </Stack>
  );
};

export const CoverageAssistantPredictions = ({ filters }: CoverageAssistantPredictionsProps) => {
  const formId = useUuid();
  const navigate = useNavigate();
  const { cache, loader } = useCuriosityConfig();
  const { t: tWidget } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'CoverageAssistantWidget' });
  const { enqueueSuccessSnackbar, enqueueErrorSnackbar, onError } = useCustomSnackbar();
  const [cacheVersion, setCacheVersion] = useState(0);

  const commodityGroupIds = filters?.commodityGroups;
  useEffect(() => {
    if (!commodityGroupIds || commodityGroupIds.length === 0) return;
    const missing = commodityGroupIds.filter((id) => !cache.has(CuriosityNamespaceType.COMMODITY_GROUP, id));
    if (missing.length === 0) return;
    let cancelled = false;
    loader
      .preload({ ns: CuriosityNamespaceType.COMMODITY_GROUP, keys: missing })
      .then(() => {
        if (!cancelled) setCacheVersion((v) => v + 1);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [cache, loader, commodityGroupIds]);

  const normalizedFilters = useMemo<NormalizedFiltersState | null>(() => {
    if (!filters) return null;

    const payload = toCoverageAssistantFiltersPayload(filters, cache);
    const paths = payload.commodityGroups.map((id) => getCommodityGroupPath(id, cache));
    const resolvedPaths = paths.filter((path): path is string[] => Array.isArray(path) && path.length > 0);
    const allPathsResolved = resolvedPaths.length === paths.length;
    const topLevels = new Set(resolvedPaths.map((path) => path[0]));
    const hasMultipleTopLevel = allPathsResolved && topLevels.size > 1;
    const errorCode = payload.error?.code ?? (hasMultipleTopLevel ? MULTIPLE_TOP_LEVEL_PATH_ERROR_CODE : null);
    const hasQueryInputs = payload.commodityGroups.length > 0 && payload.timeframe.length === 2;

    return {
      payload,
      errorCode,
      canQuery: hasQueryInputs && !errorCode,
      canSubmit: !!payload.commodityGroupId && !errorCode,
    };
    // cacheVersion is a sentinel to recompute when curiosity cache mutates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, filters, cacheVersion]);

  const handleContractDemandsCreated = useCallback(
    async (contractDemands: { sequenceNumber?: number; id: string }[]) => {
      if (contractDemands.length === 0) return;

      if (contractDemands.length === 1) {
        const contractDemandId = contractDemands[0].id;
        navigate(RouteId.contractDemandDetail(contractDemandId));
        return;
      }

      const sequenceNumbers = contractDemands
        .map((cd) => cd.sequenceNumber)
        .filter((sn): sn is number => isFiniteNumber(sn))
        .sort((a, b) => a - b);

      if (sequenceNumbers.length > 0) {
        const rules = [
          {
            field: TsrctContractDemandFilterField.sequenceNumber,
            operator: Operator.INEQ,
            value: sequenceNumbers,
          },
        ];
        const encodedQuery = await encodeQueryRules(rules);
        navigate(RouteId.contractDemandQuery(encodedQuery));
      }
    },
    [navigate],
  );

  const mutation = CONTRACT_DEMAND_CREATE_FROM_COVERAGE_WITH_RULE_BUILDER;

  const [createContractDemand, { loading: createLoading }] = useMutation(mutation, {
    onCompleted: async (data) => {
      if (data.tesseract.contractDemandCreateFromCoveragePrediction.length == 0) {
        enqueueErrorSnackbar(tWidget('noDemandsCreatedError'));
        return;
      }

      enqueueSuccessSnackbar(tWidget('saveSuccess'));
      const contractDemands = data.tesseract.contractDemandCreateFromCoveragePrediction;
      if (contractDemands) {
        await handleContractDemandsCreated(contractDemands);
      }
    },
    onError,
  });

  const onSubmit = async (data: CommodityUndercoverages) => {
    if (!normalizedFilters?.canSubmit) return;

    await createContractDemand({
      variables: {
        data: {
          commodities: data.predictions
            .filter((prediction) => prediction.selected === true)
            .map((prediction) => ({
              commodityId: prediction.commodityId,
              commodityGroupId: prediction.commodityGroupId,
              organizationId: prediction.organizationId,
              quantity: prediction.undercoveredQuantity.toString(),
              unitId: prediction.unitId,
              priceUnit: prediction.priceUnit.toString(),
              baselinePricePerPriceUnit: prediction.baselinePricePerPriceUnit?.toString(),
              minimumOrderQuantity: prediction.minimumOrderQuantity.toString(),
              qualifiedSuppliers: prediction.qualifiedSupplierIds ?? [],
              title: prediction.title ?? '',
              description: prediction.description,
              currency: prediction.currency as Currency,
              validFrom: new Date(prediction.validFrom),
              validTo: new Date(prediction.validTo),
            })),
          commodityGroupId: normalizedFilters.payload.commodityGroupId!,
          summary: data.summary,
          targetTimeframe: {
            validFrom: normalizedFilters.payload.timeframe[0],
            validTo: normalizedFilters.payload.timeframe[1],
          },
        },
      },
    });
  };

  return (
    <FormSchemaProvider
      id={formId}
      onSubmit={onSubmit}
      schema={commodityUndercoveragesSchema}
      defaultValues={{ summary: '', predictions: [] }}
    >
      <CoverageAssistantPredictionsForm normalizedFilters={normalizedFilters} createLoading={createLoading} />
    </FormSchemaProvider>
  );
};
